import throttle from 'lodash.throttle';
import { useEffect, useRef, useState } from 'react';

type StickyHeaderOptions = {
  stickyStartFrom?: string | HTMLElement | null;
  throttleDelay?: number;
  disabledScrollEffects?: boolean;
};

export function useStickyHeader({
  stickyStartFrom,
  throttleDelay = 200,
  disabledScrollEffects = false,
}: StickyHeaderOptions) {
  const [isSticky, setIsSticky] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [hasTransition, setHasTransition] = useState(false);

  const isFirstScrollUpRef = useRef(true);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const trackedElement =
      typeof stickyStartFrom === 'string'
        ? document.querySelector(stickyStartFrom)
        : stickyStartFrom;

    if (!trackedElement) return;

    const handleScroll = throttle(() => {
      const currentScrollY = window.scrollY;
      const isScrollDown = currentScrollY > lastScrollYRef.current;
      const elementBottom = trackedElement.getBoundingClientRect().bottom;
      const shouldBeSticky = elementBottom <= 0;

      if (shouldBeSticky) {
        setIsSticky(true);

        if (isScrollDown || disabledScrollEffects) {
          setIsScrolledUp(false);
        } else {
          setIsScrolledUp(true);
          setHasTransition(true);

          if (isFirstScrollUpRef.current) {
            isFirstScrollUpRef.current = false;
          }
        }
      } else {
        setIsSticky(false);
        setHasTransition(false);
        isFirstScrollUpRef.current = true;
      }

      lastScrollYRef.current = currentScrollY;
    }, throttleDelay);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { isSticky, isScrolledUp, hasTransition };
}
